import {useMutation} from '@apollo/react-hooks';

import type {ReactionType} from 'nutshell-graphql-types';
import type {
    CreateReaction as CreateReactionMutation,
    CreateReactionVariables as CreateReactionMutationVariables,
    SessionUser,
} from 'nutshell-graphql-types';

import CREATE_REACTION from '../../../timeline/reactions/graphql/mutations/create-reaction.graphql';

import ACTIVITY_MODAL_FRAGMENT from '../fragments/activity-for-modal-fragment.graphql';

export function useCreateReaction(): (arg1: {
    changeLogId: string;
    reactionType: ReactionType;
    user: SessionUser;
    activityId: string;
}) => Promise<any> {
    const [createReaction] = useMutation<CreateReactionMutation, CreateReactionMutationVariables>(
        CREATE_REACTION
    );

    return (reactionInput: {
        changeLogId: string;
        reactionType: ReactionType;
        user: SessionUser;
        activityId: string;
    }) => {
        return createReaction({
            variables: {
                input: {
                    changeLogId: reactionInput.changeLogId,
                    userId: reactionInput.user.id,
                    type: reactionInput.reactionType,
                },
            },
            optimisticResponse: {
                // @ts-expect-error
                __typename: 'Mutation',
                reactionCreate: {
                    __typename: 'ReactionCreatePayload',
                    reaction: {
                        __typename: 'Reaction',
                        id: `temp-${Date.now()}-${Math.random()}`,
                        createdTime: '',
                        creatorUser: {
                            __typename: 'User',
                            id: reactionInput.user.id,
                            name: reactionInput.user.name,
                        },
                        reactionType: reactionInput.reactionType,
                    },
                },
            },
            update: (proxy, mutationResults) => {
                const activityModalFragment = proxy.readFragment({
                    id: reactionInput.activityId,
                    fragmentName: 'ActivityModalFragment',
                    fragment: ACTIVITY_MODAL_FRAGMENT,
                });

                // @ts-expect-error
                if (activityModalFragment && activityModalFragment.comments) {
                    const newReaction =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.reactionCreate &&
                        mutationResults.data.reactionCreate.reaction;

                    if (!newReaction) return;

                    let updatedData: Record<string, any> = {};

                    // @ts-expect-error
                    const commentIndex = activityModalFragment.comments.edges.findIndex(
                        // @ts-expect-error
                        (comment) =>
                            comment.node.changeLogEntry &&
                            comment.node.changeLogEntry.id === reactionInput.changeLogId
                    );

                    if (commentIndex !== -1) {
                        // @ts-expect-error
                        const updatedComments = [...activityModalFragment.comments.edges];

                        // Add the new reaction to this comment's list of reactions
                        updatedComments[commentIndex].node.reactions.push({
                            ...newReaction,
                            __typename: 'Reaction',
                        });

                        updatedData = {
                            comments: {
                                // @ts-expect-error
                                totalCount: activityModalFragment.comments.totalCount,
                                edges: updatedComments,
                                __typename: 'CommentConnection',
                            },
                        };
                    }

                    proxy.writeFragment({
                        id: reactionInput.activityId,
                        fragment: ACTIVITY_MODAL_FRAGMENT,
                        fragmentName: 'ActivityModalFragment',
                        data: {
                            ...activityModalFragment,
                            __typename: 'Activity',
                            ...updatedData,
                        },
                    });
                }
            },
        });
    };
}
